import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowOutward as ArrowOutwardIcon,
  Assignment as AssignmentIcon,
  AutoGraph as AutoGraphIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as EmojiEventsIcon,
  EventNote as EventNoteIcon,
  Group as GroupIcon,
  Quiz as QuizIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { TEACHER_DASHBOARD, GOOGLE_CALENDAR_EVENTS } from '../../config/Config';

const shellCardSx = {
  borderRadius: 5,
  border: '1px solid rgba(148, 163, 184, 0.18)',
  boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)',
  backgroundColor: '#ffffff',
};

const actionButtonSx = {
  px: 3,
  py: 1.25,
  borderRadius: 999,
  fontWeight: 700,
  textTransform: 'none',
  boxShadow: '0 14px 30px rgba(79, 70, 229, 0.22)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 18px 36px rgba(79, 70, 229, 0.28)',
  },
};

const TeacherDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [dashboardData, setDashboardData] = useState({
    total_students: 0,
    active_tests: 0,
    pending_grading: 0,
    upcoming_deadlines: 0,
    awards_given: 0,
    recent_activity: [],
  });
  const [calendarEventsCount, setCalendarEventsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchDashboard = async () => {
      try {
        const [res, calendarRes] = await Promise.all([
          api.get(TEACHER_DASHBOARD),
          api.get(GOOGLE_CALENDAR_EVENTS).catch(() => ({ data: { events: [], total: 0 } })),
        ]);

        if (!cancelled && res.data) {
          setDashboardData({
            total_students: res.data.total_students ?? 0,
            active_tests: res.data.active_tests ?? 0,
            pending_grading: res.data.pending_grading ?? 0,
            upcoming_deadlines: res.data.upcoming_deadlines ?? 0,
            awards_given: res.data.awards_given ?? 0,
            recent_activity: res.data.recent_activity ?? [],
          });
        }

        if (!cancelled && calendarRes?.data) {
          const total = calendarRes.data.total ?? (calendarRes.data.events?.length ?? 0);
          setCalendarEventsCount(total);
        }
      } catch (_) {
        if (!cancelled) {
          setDashboardData((prev) => ({ ...prev, recent_activity: [] }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDashboard();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const priorityConfig = useMemo(() => {
    if (dashboardData.pending_grading > 0) {
      return {
        title: `${dashboardData.pending_grading} ${dashboardData.pending_grading === 1 ? 'test needs' : 'tests need'} review`,
        description: 'Jump back into grading and keep feedback moving for your students.',
        ctaLabel: 'Review assignments',
        ctaPath: '/teacher/assignments?tab=ready_for_review',
        accent: '#f59e0b',
        chip: 'Priority today',
      };
    }

    if (dashboardData.total_students === 0) {
      return {
        title: 'Your classroom is ready for its first student',
        description: 'Add students first so you can assign work, track activity, and build momentum.',
        ctaLabel: null,
        ctaPath: null,
        accent: '#2563eb',
        chip: 'Get started',
      };
    }

    if (dashboardData.active_tests === 0) {
      return {
        title: 'No active tests right now',
        description: 'Create your next test to keep practice moving and give your class a clear next step.',
        ctaLabel: 'Create test',
        ctaPath: '/teacher/tests',
        accent: '#7c3aed',
        chip: 'Next action',
      };
    }

    if (dashboardData.upcoming_deadlines > 0) {
      return {
        title: `${dashboardData.upcoming_deadlines} ${dashboardData.upcoming_deadlines === 1 ? 'deadline is' : 'deadlines are'} coming up`,
        description: 'Check assignments and upcoming work so nothing slips past your class this week.',
        ctaLabel: 'View assignments',
        ctaPath: '/teacher/assignments',
        accent: '#0f766e',
        chip: 'Coming up',
      };
    }

    return {
      title: 'No pending grading — you are all caught up',
      description: 'Everything is in a good place. Keep momentum going with a fresh test or new assignment.',
      ctaLabel: 'Create test',
      ctaPath: '/teacher/tests',
      accent: '#16a34a',
      chip: 'All clear',
    };
  }, [dashboardData]);

  const snapshotCards = useMemo(
    () => [
      {
        title: 'Total students',
        value: dashboardData.total_students,
        helper:
          dashboardData.total_students > 0
            ? `${dashboardData.total_students} ${dashboardData.total_students === 1 ? 'student in your class' : 'students in your class'}`
            : 'Add your first student to get started',
        icon: <GroupIcon />,
        accent: '#2563eb',
        path: '/teacher/students',
      },
      {
        title: 'Active tests',
        value: dashboardData.active_tests,
        helper:
          dashboardData.active_tests > 0
            ? 'Assignments and completed work are already in motion'
            : 'Create your next test to keep learning moving',
        icon: <QuizIcon />,
        accent: '#7c3aed',
        path: '/teacher/assignments',
      },
      {
        title: 'Pending grading',
        value: dashboardData.pending_grading,
        helper:
          dashboardData.pending_grading > 0
            ? 'Ready for review and final feedback'
            : 'All caught up for now',
        icon: <AssignmentIcon />,
        accent: '#f59e0b',
        path: '/teacher/assignments?tab=ready_for_review',
      },
      {
        title: 'Calendar events',
        value: calendarEventsCount,
        helper:
          calendarEventsCount > 0
            ? 'Stay ahead of upcoming classroom events'
            : 'Your schedule is clear right now',
        icon: <EventNoteIcon />,
        accent: '#0f766e',
        path: '/teacher/calendar',
      },
    ],
    [calendarEventsCount, dashboardData]
  );

  const actionCards = useMemo(
    () => [
      {
        label: 'Manage Students',
        helper: 'Add or organize your class',
        icon: <GroupIcon />,
        path: '/teacher/students',
        accent: '#2563eb',
      },
      {
        label: 'Create Test',
        helper: 'Build a test in seconds',
        icon: <QuizIcon />,
        path: '/teacher/tests',
        accent: '#7c3aed',
      },
      {
        label: 'Assignments',
        helper: 'Assign and track work',
        icon: <AssignmentIcon />,
        path: '/teacher/assignments',
        accent: '#0f766e',
      },
      {
        label: 'Awards Setup',
        helper: 'Motivate with rewards',
        icon: <EmojiEventsIcon />,
        path: '/teacher/awards',
        accent: '#ea580c',
      },
    ],
    []
  );

  const insightCards = useMemo(
    () => [
      {
        title: 'Classroom readiness',
        value:
          dashboardData.total_students > 0
            ? `${dashboardData.total_students} ${dashboardData.total_students === 1 ? 'student is' : 'students are'} ready for assigned work`
            : 'No students yet',
        helper:
          dashboardData.total_students > 0
            ? 'Your class roster is in place and ready for the next task.'
            : 'Add students first so the rest of your workflow can begin.',
        icon: <TaskAltIcon />,
        accent: '#2563eb',
      },
      {
        title: 'Work needing attention',
        value:
          dashboardData.pending_grading > 0
            ? `${dashboardData.pending_grading} ${dashboardData.pending_grading === 1 ? 'review item' : 'review items'} waiting`
            : 'Nothing waiting for review',
        helper:
          dashboardData.pending_grading > 0
            ? 'Review these first to keep feedback quick and useful.'
            : 'You are caught up on grading right now.',
        icon: <AssignmentIcon />,
        accent: '#f59e0b',
      },
      {
        title: 'Class momentum',
        value:
          dashboardData.active_tests > 0
            ? `${dashboardData.active_tests} ${dashboardData.active_tests === 1 ? 'active test' : 'active tests'} running`
            : 'No active tests yet',
        helper:
          dashboardData.active_tests > 0
            ? 'Your class already has live work in progress.'
            : 'Create and assign a test to generate fresh activity.',
        icon: <AutoGraphIcon />,
        accent: '#7c3aed',
      },
    ],
    [dashboardData]
  );

  return (
    <Box sx={{ width: '100%', px: { xs: 2, md: 3 }, py: { xs: 2.5, md: 4 } }}>
      <Container
        maxWidth={false}
        sx={{
          maxWidth: 1600,
          mx: 'auto',
          px: '0 !important',
        }}
      >
        <Stack spacing={4}>
          <Card
            sx={{
              ...shellCardSx,
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${alpha(priorityConfig.accent, 0.12)} 0%, #ffffff 55%)`,
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Stack spacing={2}>
                    <Chip
                      label={priorityConfig.chip}
                      sx={{
                        alignSelf: 'flex-start',
                        borderRadius: 999,
                        backgroundColor: alpha(priorityConfig.accent, 0.14),
                        color: priorityConfig.accent,
                        fontWeight: 700,
                      }}
                    />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
                        Welcome back, {user?.first_name || 'Teacher'}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.2 }}>
                        {priorityConfig.title}
                      </Typography>
                      <Typography sx={{ color: '#64748b', lineHeight: 1.7, maxWidth: 720 }}>
                        {priorityConfig.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack spacing={1.5} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                    {priorityConfig.ctaLabel && priorityConfig.ctaPath && (
                      <Button
                        variant="contained"
                        endIcon={<ArrowOutwardIcon />}
                        onClick={() => handleNavigation(priorityConfig.ctaPath)}
                        sx={actionButtonSx}
                      >
                        {priorityConfig.ctaLabel}
                      </Button>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                      {[
                        `${dashboardData.upcoming_deadlines} upcoming ${dashboardData.upcoming_deadlines === 1 ? 'deadline' : 'deadlines'}`,
                        `${dashboardData.awards_given} ${dashboardData.awards_given === 1 ? 'award given' : 'awards given'}`,
                      ].map((item) => (
                        <Chip
                          key={item}
                          label={loading ? 'Loading...' : item}
                          sx={{
                            backgroundColor: '#ffffff',
                            border: '1px solid rgba(148,163,184,0.2)',
                            color: '#334155',
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.75 }}>
              Classroom snapshot
            </Typography>
            <Typography sx={{ color: '#64748b', mb: 2.5 }}>
              A quick view of what is active, what is clear, and where to act next.
            </Typography>
            <Grid container spacing={3}>
              {snapshotCards.map((card) => (
                <Grid item xs={12} sm={6} lg={3} key={card.title}>
                  <Card
                    onClick={() => handleNavigation(card.path)}
                    sx={{
                      ...shellCardSx,
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 24px 56px rgba(15, 23, 42, 0.12)',
                        borderColor: alpha(card.accent, 0.35),
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack spacing={2.2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Avatar sx={{ bgcolor: alpha(card.accent, 0.12), color: card.accent }}>
                            {card.icon}
                          </Avatar>
                          {!loading && card.title === 'Pending grading' && card.value === 0 && (
                            <Chip
                              icon={<CheckCircleIcon sx={{ color: '#15803d !important' }} />}
                              label="Caught up"
                              size="small"
                              sx={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 700 }}
                            />
                          )}
                        </Stack>
                        <Box>
                          <Typography sx={{ color: '#64748b', fontSize: '0.92rem', mb: 0.8 }}>{card.title}</Typography>
                          <Typography sx={{ color: '#0f172a', fontWeight: 800, fontSize: '2rem', lineHeight: 1 }}>
                            {loading ? '--' : card.value}
                          </Typography>
                        </Box>
                        <Typography sx={{ color: '#64748b', lineHeight: 1.65 }}>{card.helper}</Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.75 }}>
              Take action
            </Typography>
            <Typography sx={{ color: '#64748b', mb: 2.5 }}>
              Use the same tools you already have, surfaced here with the clearest next step.
            </Typography>
            <Grid container spacing={3}>
              {actionCards.map((action) => (
                <Grid item xs={12} sm={6} lg={3} key={action.label}>
                  <Card
                    onClick={() => handleNavigation(action.path)}
                    sx={{
                      ...shellCardSx,
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 24px 56px rgba(15, 23, 42, 0.12)',
                        borderColor: alpha(action.accent, 0.35),
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        <Avatar sx={{ bgcolor: alpha(action.accent, 0.12), color: action.accent, width: 52, height: 52 }}>
                          {action.icon}
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {action.label}
                        </Typography>
                        <Typography sx={{ color: '#64748b', lineHeight: 1.7 }}>
                          {action.helper}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <Card sx={{ ...shellCardSx, height: '100%' }}>
                <CardContent sx={{ p: 3.2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.75 }}>
                    Class insights
                  </Typography>
                  <Typography sx={{ color: '#64748b', mb: 2.5 }}>
                    Derived from your current classroom totals so you can act without digging.
                  </Typography>
                  <Stack spacing={2}>
                    {insightCards.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          p: 2,
                          borderRadius: 4,
                          border: '1px solid rgba(148,163,184,0.18)',
                          backgroundColor: alpha(item.accent, 0.05),
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Avatar sx={{ width: 42, height: 42, bgcolor: alpha(item.accent, 0.12), color: item.accent }}>
                            {item.icon}
                          </Avatar>
                          <Box>
                            <Typography sx={{ color: '#475569', fontSize: '0.86rem', mb: 0.35 }}>{item.title}</Typography>
                            <Typography sx={{ color: '#0f172a', fontWeight: 700, mb: 0.45 }}>{item.value}</Typography>
                            <Typography sx={{ color: '#64748b', lineHeight: 1.65 }}>{item.helper}</Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Card sx={{ ...shellCardSx, height: '100%' }}>
                <CardContent sx={{ p: 3.2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.75 }}>
                    Recent activity
                  </Typography>
                  <Typography sx={{ color: '#64748b', mb: 2.5 }}>
                    A quick timeline of what has happened most recently in your classroom.
                  </Typography>

                  {dashboardData.recent_activity && dashboardData.recent_activity.length > 0 ? (
                    <Stack spacing={1.25}>
                      {dashboardData.recent_activity.map((activity, index) => {
                        const isTest = activity.type === 'test';
                        const isAssignment = activity.type === 'assignment';
                        const accent = isTest ? '#2563eb' : isAssignment ? '#7c3aed' : '#0f766e';
                        const icon = isTest ? <QuizIcon fontSize="small" /> : isAssignment ? <AssignmentIcon fontSize="small" /> : <GroupIcon fontSize="small" />;

                        return (
                          <Box
                            key={activity.id || index}
                            sx={{
                              p: 2,
                              borderRadius: 4,
                              border: '1px solid rgba(148,163,184,0.18)',
                              backgroundColor: '#ffffff',
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(accent, 0.12), color: accent }}>
                                {icon}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: '#0f172a', fontWeight: 700 }}>{activity.title}</Typography>
                                <Typography sx={{ color: '#64748b', fontSize: '0.92rem' }}>{activity.time}</Typography>
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        minHeight: 220,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 4,
                        border: '1px dashed rgba(148,163,184,0.3)',
                        backgroundColor: '#f8fafc',
                        textAlign: 'center',
                        p: 3,
                      }}
                    >
                      <Stack spacing={1.5} alignItems="center" maxWidth={420}>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main', width: 54, height: 54 }}>
                          <AutoGraphIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          No recent activity yet
                        </Typography>
                        <Typography sx={{ color: '#64748b', lineHeight: 1.7 }}>
                          Start by creating or assigning a test so this space can begin showing classroom movement.
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={() => handleNavigation('/teacher/tests')}
                          sx={actionButtonSx}
                        >
                          Create test
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
};

const TeacherDashboardWithErrorBoundary = (props) => (
  <ErrorBoundary>
    <TeacherDashboard {...props} />
  </ErrorBoundary>
);

export default TeacherDashboardWithErrorBoundary;
